import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Package, AlertTriangle, Layers } from 'lucide-react';
import { BinLocation, BinLocationGroup, RackStructure, compareSegments } from './types';

interface RackGridProps {
    rackStructure: RackStructure;
    selectedBinCodes: string[];
    onSelectionChange: (binCodes: string[]) => void;
    filteredBinCodes: string[];
}

interface CellPosition {
    vertical: string;
    shelf: string;
}

/**
 * RackGrid Component (FR-104)
 * 
 * Optimized for large screens - displays as many locations as possible
 * - X-axis (horizontal): Verticals (V01, V02, V03, ...) from left to right
 * - Y-axis (vertical): Shelves (H09, H08, ..., H01, H00) from top to bottom (highest shelf at top)
 * - Excel-like selection: Click, Ctrl+Click, Shift+Click, Drag-to-select
 */
export const RackGrid: React.FC<RackGridProps> = ({
    rackStructure,
    selectedBinCodes,
    onSelectionChange,
    filteredBinCodes
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState<CellPosition | null>(null);
    const [dragCurrent, setDragCurrent] = useState<CellPosition | null>(null);
    const [hoveredLocation, setHoveredLocation] = useState<BinLocation | null>(null);
    const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
    const gridRef = useRef<HTMLDivElement>(null);
    const lastClickedRef = useRef<string | null>(null);

    // Create a map for quick lookup: "vertical-shelf" -> BinLocation (primary)
    const locationMap = new Map<string, BinLocation>();
    rackStructure.locations.forEach((loc: BinLocation) => {
        const key = `${loc.vertical}-${loc.shelf}`;
        // Only store the first location for each cell (for backward compatibility)
        if (!locationMap.has(key)) {
            locationMap.set(key, loc);
        }
    });

    /**
     * Group locations by cell (vertical-shelf) to identify 4D cells with multiple drawers.
     * This allows the grid to show a single cell with an indicator when multiple drawers exist.
     */
    const locationGroups = useMemo(() => {
        const groups = new Map<string, BinLocationGroup>();

        rackStructure.locations.forEach((loc: BinLocation) => {
            const cellKey = `${loc.vertical}-${loc.shelf}`;

            if (!groups.has(cellKey)) {
                groups.set(cellKey, {
                    cellKey,
                    rackNumber: loc.rackNumber,
                    vertical: loc.vertical,
                    shelf: loc.shelf,
                    locations: [],
                    primaryLocation: loc,
                    hasMultipleDrawers: false,
                    drawerCount: 0
                });
            }

            const group = groups.get(cellKey)!;
            group.locations.push(loc);
            group.drawerCount = group.locations.length;
            group.hasMultipleDrawers = group.drawerCount > 1;

            // Sort locations by drawer (natural-sort: numerisk eller alfabetisk)
            group.locations.sort((a, b) => compareSegments(a.drawer ?? '', b.drawer ?? ''));
            // Primary location is always the first one
            group.primaryLocation = group.locations[0];
        });

        return groups;
    }, [rackStructure.locations]);

    /**
     * Get all BinCodes for a cell (including all drawers)
     */
    const getAllBinCodesForCell = (vertical: string, shelf: string): string[] => {
        const cellKey = `${vertical}-${shelf}`;
        const group = locationGroups.get(cellKey);
        return group ? group.locations.map(loc => loc.binCode) : [];
    };

    // Sort shelves in DESCENDING order (highest shelf at top, lowest at bottom)
    const sortedShelves = useMemo(
        () => [...rackStructure.shelves].sort((a, b) => compareSegments(b, a)),
        [rackStructure.shelves]
    );

    // Sort verticals in ascending order (left to right: V01, V02, V03, ...)
    const sortedVerticals = useMemo(
        () => [...rackStructure.verticals].sort(compareSegments),
        [rackStructure.verticals]
    );

    /**
     * Check if a location is selectable (not filtered out)
     */
    const isSelectable = (binCode: string): boolean => {
        return filteredBinCodes.includes(binCode);
    };

    /**
     * Check if a location is selected
     */
    const isSelected = (binCode: string): boolean => {
        return selectedBinCodes.includes(binCode);
    };

    /**
     * Get location by grid position
     */
    const getLocationAt = (vertical: string, shelf: string): BinLocation | undefined => {
        const key = `${vertical}-${shelf}`;
        return locationMap.get(key);
    };

    /**
     * Get all locations in a rectangular range. Bruker indeks i sortedVerticals/sortedShelves
     * for å støtte ikke-numeriske segment-koder (f.eks. "SL", "V-A4").
     */
    const getLocationsInRange = (start: CellPosition, end: CellPosition): string[] => {
        const vIdxStart = sortedVerticals.indexOf(start.vertical);
        const vIdxEnd = sortedVerticals.indexOf(end.vertical);
        const sIdxStart = sortedShelves.indexOf(start.shelf);
        const sIdxEnd = sortedShelves.indexOf(end.shelf);
        if (vIdxStart < 0 || vIdxEnd < 0 || sIdxStart < 0 || sIdxEnd < 0) return [];

        const minV = Math.min(vIdxStart, vIdxEnd);
        const maxV = Math.max(vIdxStart, vIdxEnd);
        const minS = Math.min(sIdxStart, sIdxEnd);
        const maxS = Math.max(sIdxStart, sIdxEnd);

        const locations: string[] = [];
        for (let v = minV; v <= maxV; v++) {
            for (let s = minS; s <= maxS; s++) {
                const loc = getLocationAt(sortedVerticals[v], sortedShelves[s]);
                if (loc && isSelectable(loc.binCode)) {
                    locations.push(loc.binCode);
                }
            }
        }
        return locations;
    };

    /**
     * Handle cell click - selects all drawers in a 4D cell
     */
    const handleCellClick = (location: BinLocation, event: React.MouseEvent) => {
        // Get all bin codes for this cell (all drawers)
        const cellBinCodes = getAllBinCodesForCell(location.vertical, location.shelf);
        const selectableCodes = cellBinCodes.filter(code => isSelectable(code));

        if (selectableCodes.length === 0) return;

        const isCtrlKey = event.ctrlKey || event.metaKey;
        const isShiftKey = event.shiftKey;

        if (isShiftKey && lastClickedRef.current) {
            // Shift-click: Select range from last clicked to current
            const lastLoc = rackStructure.locations.find((l: BinLocation) => l.binCode === lastClickedRef.current);
            if (lastLoc) {
                const start = { vertical: lastLoc.vertical, shelf: lastLoc.shelf };
                const end = { vertical: location.vertical, shelf: location.shelf };
                const rangeLocations = getLocationsInRange(start, end);

                // Add range to selection (union)
                const newSelection = Array.from(new Set([...selectedBinCodes, ...rangeLocations]));
                onSelectionChange(newSelection);
            }
        } else if (isCtrlKey) {
            // Ctrl-click: Toggle all drawers in this cell
            const allSelected = selectableCodes.every(code => selectedBinCodes.includes(code));

            if (allSelected) {
                // Deselect all in this cell
                const newSelection = selectedBinCodes.filter(code => !selectableCodes.includes(code));
                onSelectionChange(newSelection);
            } else {
                // Select all in this cell
                const newSelection = Array.from(new Set([...selectedBinCodes, ...selectableCodes]));
                onSelectionChange(newSelection);
            }
            lastClickedRef.current = location.binCode;
        } else {
            // Normal click: Select all drawers in this cell
            onSelectionChange(selectableCodes);
            lastClickedRef.current = location.binCode;
        }
    };

    /**
     * Handle drag start
     */
    const handleDragStart = (vertical: string, shelf: string, event: React.MouseEvent) => {
        const location = getLocationAt(vertical, shelf);
        if (!location || !isSelectable(location.binCode)) return;

        // Only start drag with left mouse button and no modifier keys
        if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey) return;

        event.preventDefault();
        setIsDragging(true);
        setDragStart({ vertical, shelf });
        setDragCurrent({ vertical, shelf });

        // Start with single cell selected
        onSelectionChange([location.binCode]);
        lastClickedRef.current = location.binCode;
    };

    /**
     * Handle drag move
     */
    const handleDragMove = (vertical: string, shelf: string) => {
        if (!isDragging || !dragStart) return;

        setDragCurrent({ vertical, shelf });

        // Select all cells in the drag rectangle
        const rangeLocations = getLocationsInRange(dragStart, { vertical, shelf });
        onSelectionChange(rangeLocations);
    };

    /**
     * Handle drag end
     */
    const handleDragEnd = useCallback(() => {
        setIsDragging(false);
        setDragStart(null);
        setDragCurrent(null);
    }, []);

    // Add global mouse up listener for drag end
    useEffect(() => {
        if (isDragging) {
            window.addEventListener('mouseup', handleDragEnd);
            return () => window.removeEventListener('mouseup', handleDragEnd);
        }
    }, [isDragging, handleDragEnd]);

    /**
     * Handle cell hover for tooltip
     */
    const handleCellHover = (location: BinLocation, event: React.MouseEvent) => {
        setHoveredLocation(location);
        setTooltipPosition({ x: event.clientX, y: event.clientY });
    };

    const handleCellLeave = () => {
        setHoveredLocation(null);
        setTooltipPosition(null);
    };

    /**
     * Check if cell is in current drag selection (indeks-basert for å støtte non-numeriske segmenter)
     */
    const isInDragSelection = (vertical: string, shelf: string): boolean => {
        if (!isDragging || !dragStart || !dragCurrent) return false;

        const vIdx = sortedVerticals.indexOf(vertical);
        const sIdx = sortedShelves.indexOf(shelf);
        const vStart = sortedVerticals.indexOf(dragStart.vertical);
        const vEnd = sortedVerticals.indexOf(dragCurrent.vertical);
        const sStart = sortedShelves.indexOf(dragStart.shelf);
        const sEnd = sortedShelves.indexOf(dragCurrent.shelf);
        if (vIdx < 0 || sIdx < 0 || vStart < 0 || vEnd < 0 || sStart < 0 || sEnd < 0) return false;

        return vIdx >= Math.min(vStart, vEnd) && vIdx <= Math.max(vStart, vEnd)
            && sIdx >= Math.min(sStart, sEnd) && sIdx <= Math.max(sStart, sEnd);
    };

    /**
     * Get cell class names based on state
     */
    const getCellClassName = (location: BinLocation | undefined, vertical: string, shelf: string): string => {
        if (!location) {
            return 'bg-gray-50 border border-dashed border-gray-200';
        }

        const selectable = isSelectable(location.binCode);
        const selected = isSelected(location.binCode);
        const inDrag = isInDragSelection(vertical, shelf);

        const baseClasses = 'relative border transition-all cursor-pointer select-none';
        
        if (!selectable) {
            // Filtered out - not selectable
            return `${baseClasses} bg-gray-100 border-dashed border-gray-300 opacity-50 pointer-events-none`;
        }

        if (selected || inDrag) {
            // Selected
            return `${baseClasses} bg-blue-100 border-blue-500 border-2 shadow-sm`;
        }

        // Normal selectable
        return `${baseClasses} bg-white border-gray-300 hover:border-blue-400 hover:shadow`;
    };

    if (rackStructure.locations.length === 0) {
        return (
            <div className="flex items-center justify-center h-64 text-gray-500">
                <p>Ingen lokasjoner å vise</p>
            </div>
        );
    }

    return (
        <div className="w-full h-full flex flex-col">
            {/* Grid container - fills all available space */}
            <div 
                ref={gridRef}
                className="w-full h-full border border-gray-300 bg-white rounded-lg p-4 overflow-auto"
                style={{ userSelect: 'none' }}
            >
                {/* Header with vertical numbers - sticky */}
                <div className="flex mb-3 sticky top-0 bg-white z-10 border-b border-gray-200 pb-1">
                    <div className="w-20 flex-shrink-0" /> {/* Spacer for shelf labels */}
                    {sortedVerticals.map(vertical => (
                        <div
                            key={vertical}
                            className="w-36 text-center font-semibold text-base text-gray-700 flex-shrink-0"
                        >
                            V{/^\d+$/.test(vertical) ? vertical.padStart(2, '0') : vertical}
                        </div>
                    ))}
                </div>

                {/* Rack visualization */}
                <div className="space-y-1.5">
                    {sortedShelves.map((shelf) => (
                        <div key={shelf} className="flex items-center">
                            {/* Shelf label - sticky */}
                            <div className="w-20 text-right pr-4 font-semibold text-base text-gray-700 flex-shrink-0 sticky left-0 bg-white z-5">
                                H{/^\d+$/.test(shelf) ? shelf.padStart(2, '0') : shelf}
                            </div>

                            {/* Cells for this shelf */}
                            {sortedVerticals.map(vertical => {
                                const location = getLocationAt(vertical, shelf);
                                const cellKey = `${vertical}-${shelf}`;
                                const group = locationGroups.get(cellKey);
                                const cellClass = getCellClassName(location, vertical, shelf);

                                return (
                                    <div
                                        key={`${vertical}-${shelf}`}
                                        className={`w-36 h-30 ${cellClass} flex flex-col items-center justify-center p-2 text-sm flex-shrink-0`}
                                        onClick={(e) => location && handleCellClick(location, e)}
                                        onMouseDown={(e) => handleDragStart(vertical, shelf, e)}
                                        onMouseEnter={() => {
                                            if (isDragging) {
                                                handleDragMove(vertical, shelf);
                                            }
                                        }}
                                        onMouseMove={(e) => location && handleCellHover(location, e)}
                                        onMouseLeave={handleCellLeave}
                                    >
                                        {location ? (
                                            <>
                                                {/* BinCode - show primary location */}
                                                <div className="font-mono font-semibold text-sm text-gray-800">
                                                    {group?.primaryLocation.binCode ?? location.binCode}
                                                </div>

                                                {/* 4D Drawer indicator - shown when cell has multiple drawers */}
                                                {group?.hasMultipleDrawers && (
                                                    <div
                                                        className="flex items-center justify-center bg-purple-100 border border-purple-300 rounded px-1.5 py-0.5 mt-1"
                                                        title={`${group.drawerCount} skuffer i denne lokasjonen`}
                                                    >
                                                        <Layers className="w-3 h-3 text-purple-600" />
                                                        <span className="text-xs font-semibold text-purple-700 ml-1">
                                                            {group.drawerCount}
                                                        </span>
                                                    </div>
                                                )}

                                                {/* Indicators - fixed height to maintain consistent spacing */}
                                                <div className="flex items-center justify-center space-x-1.5 mt-1.5 h-6">
                                                    {/* Dedicated location (has ItemNo) */}
                                                    {location.itemNo && (
                                                        <div title="Dedikert lokasjon">
                                                            <Package className="w-4 h-4 text-green-600" />
                                                        </div>
                                                    )}

                                                    {/* Low priority indicator */}
                                                    {location.pickPriorityNo && location.pickPriorityNo > 50 && (
                                                        <div title="Lav prioritet">
                                                            <AlertTriangle className="w-4 h-4 text-yellow-600" />
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Size/Type info */}
                                                <div className="text-xs text-gray-500 mt-1">
                                                    {location.binSizeNo}/{location.binTypeNo}
                                                </div>
                                            </>
                                        ) : (
                                            <div className="text-gray-400 text-[8px]">-</div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>

            {/* Tooltip */}
            {hoveredLocation && tooltipPosition && (() => {
                const hoveredCellKey = `${hoveredLocation.vertical}-${hoveredLocation.shelf}`;
                const hoveredGroup = locationGroups.get(hoveredCellKey);

                return (
                    <div
                        className="fixed z-50 bg-gray-900 text-white p-3 rounded shadow-lg text-xs max-w-xs"
                        style={{
                            left: tooltipPosition.x + 10,
                            top: tooltipPosition.y + 10,
                            pointerEvents: 'none'
                        }}
                    >
                        <div className="font-semibold mb-1">{hoveredLocation.binCode}</div>
                        {hoveredLocation.description1 && (
                            <div className="mb-1">{hoveredLocation.description1}</div>
                        )}

                        {/* 4D Drawer list - shown when cell has multiple drawers */}
                        {hoveredGroup?.hasMultipleDrawers && (
                            <div className="mb-2 pb-2 border-b border-gray-700">
                                <div className="text-purple-300 font-semibold mb-1">
                                    📦 {hoveredGroup.drawerCount} skuffer i denne cellen:
                                </div>
                                <div className="space-y-0.5 max-h-32 overflow-y-auto">
                                    {hoveredGroup.locations.map((loc, idx) => (
                                        <div key={loc.binCode} className="text-gray-400 text-[10px] font-mono">
                                            {loc.binCode} {loc.drawer !== undefined && `(Skuff ${loc.drawer})`}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="space-y-0.5 text-gray-300">
                            <div>Størrelse: {hoveredLocation.binSizeNo}</div>
                            <div>Type: {hoveredLocation.binTypeNo}</div>
                            {hoveredLocation.resourceNo && (
                                <div>Ressurs: {hoveredLocation.resourceNo}</div>
                            )}
                            {hoveredLocation.stcZoneID && (
                                <div>Sone: {hoveredLocation.stcZoneID}</div>
                            )}
                            {hoveredLocation.pickPriorityNo && (
                                <div>Plukkprioritet: {hoveredLocation.pickPriorityNo}</div>
                            )}
                            {hoveredLocation.itemNo && (
                                <div className="text-green-300 font-semibold">
                                    Dedikert til: {hoveredLocation.itemNo}
                                </div>
                            )}
                            <div>
                                Plukkbar: {hoveredLocation.pickableYesNo ? 'Ja' : 'Nei'}
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Compact footer with legend and selection info */}
            <div className="mt-2 flex items-center justify-between text-xs gap-4">
                {/* Legend */}
                <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-1.5">
                        <div className="w-3 h-3 bg-white border border-gray-300 rounded" />
                        <span>Normal</span>
                    </div>
                    <div className="flex items-center space-x-1.5">
                        <div className="w-3 h-3 bg-blue-100 border-2 border-blue-500 rounded" />
                        <span>Valgt</span>
                    </div>
                    <div className="flex items-center space-x-1.5">
                        <div className="w-3 h-3 bg-gray-100 border border-dashed border-gray-300 rounded opacity-50" />
                        <span>Filtrert</span>
                    </div>
                    <div className="flex items-center space-x-1.5">
                        <Package className="w-3 h-3 text-green-600" />
                        <span>Dedikert</span>
                    </div>
                    <div className="flex items-center space-x-1.5">
                        <div className="flex items-center bg-purple-100 border border-purple-300 rounded px-1 py-0.5">
                            <Layers className="w-3 h-3 text-purple-600" />
                            <span className="text-[10px] font-semibold text-purple-700 ml-0.5">3</span>
                        </div>
                        <span>4D (skuffer)</span>
                    </div>
                </div>

                {/* Selection info */}
                <div className="text-gray-600 whitespace-nowrap">
                    <span className="font-semibold">
                        {selectedBinCodes.length}/{filteredBinCodes.length}
                    </span>
                    {isDragging && (
                        <span className="ml-2 text-blue-600 font-semibold">
                            (Dra)
                        </span>
                    )}
                </div>
            </div>

            {/* Compact instructions */}
            <div className="mt-1 text-xs text-gray-500">
                💡 <strong>Klikk</strong>=velg | <strong>Ctrl+Klikk</strong>=flere | <strong>Shift+Klikk</strong>=område | <strong>Dra</strong>=Excel
            </div>
        </div>
    );
};
